import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import ShareAction from "@/components/ShareAction";
import {
  Link as LinkIcon,
  Share2,
  Copy,
  MessageCircle,
  Mail,
  Smartphone,
  ExternalLink,
  Eye,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";

// Simplified "Lead Machine" card for the dashboard.
// UX goal: clean dashboard. Single primary CTA ("Condividi link") that opens a
// powerful modal identical in style to "Condividi preventivo e contratto".
// Performance stats remain inline so the user sees the outcome without clicking.
export default function LeadMachineCard() {
  const [linkData, setLinkData] = useState(null);
  const [stats, setStats] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const fullUrl = linkData ? `${window.location.origin}${linkData.path}` : "";
  const canWebShare = typeof navigator !== "undefined" && !!navigator.share;

  useEffect(() => {
    api.get("/users/me/lead-link").then((r) => setLinkData(r.data)).catch(() => {});
    api.get("/users/me/lead-stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  // Single source of truth for the message sent on every channel (WhatsApp / Email / Web Share).
  // Bilingual IT + EN. Used unchanged across channels to keep the tone consistent.
  const getLeadMessage = (link) =>
`Buongiorno,
per poterle preparare un preventivo preciso e personalizzato, La invito a compilare questa breve scheda:

${link}

Una volta ricevuta, Le invierò il preventivo entro 48–72 ore.

---

Good morning,
to prepare a precise and personalized quote, please fill out this short form:

${link}

Once received, you will get your quote within 48–72 hours.`;

  const leadMessage = getLeadMessage(fullUrl);
  const emailSubject = "Richiesta informazioni matrimonio · Wedding inquiry";

  const copyLink = async () => {
    if (!fullUrl) return;
    setBusyAction("link");
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare il link");
    } finally { setBusyAction(""); }
  };

  const shareWhatsApp = () => {
    if (!fullUrl) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(leadMessage)}`, "_blank", "noopener");
  };

  const shareEmail = () => {
    if (!fullUrl) return;
    window.location.href = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(leadMessage)}`;
  };

  const shareNative = async () => {
    if (!fullUrl || !canWebShare) return;
    setBusyAction("system");
    try {
      await navigator.share({
        title: emailSubject,
        text: leadMessage,
        url: fullUrl,
      });
    } catch {/* user dismissed */}
    finally { setBusyAction(""); }
  };

  if (!linkData) {
    return (
      <div className="bg-white border border-border rounded-sm p-8 animate-pulse">
        <div className="h-4 bg-secondary rounded w-1/3 mb-3"></div>
        <div className="h-6 bg-secondary rounded w-2/3"></div>
      </div>
    );
  }

  const weekViews = stats?.week?.views ?? 0;
  const weekSubs = stats?.week?.submissions ?? 0;
  const weekConv = stats?.week?.conversion ?? 0;

  return (
    <>
      <section
        className="bg-white border border-border rounded-sm p-6 sm:p-8"
        data-testid="lead-machine-card"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <span className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <LinkIcon className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0">
            <span className="label-uppercase text-primary">Lead machine</span>
            <h2 className="font-serif-heading text-2xl sm:text-3xl mt-1 leading-tight">
              Ricevi richieste preventivo
            </h2>
            <p className="text-sm text-neutral-500 mt-2 max-w-xl">
              Condividi il tuo link su WhatsApp, Instagram o sito. Le richieste arrivano direttamente nel CRM.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setShareOpen(true)}
            className="h-11 rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground px-5"
            data-testid="lead-machine-share-button"
          >
            <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Condividi link
          </Button>
        </div>

        {/* Performance stats — last 7 days */}
        {stats && (
          <div className="mt-6 border-t border-border pt-5" data-testid="lead-machine-stats">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-primary" strokeWidth={1.5} />
              <span className="label-uppercase">Performance · ultimi 7 giorni</span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Stat label="Visualizzazioni" value={weekViews} />
              <Stat label="Richieste" value={weekSubs} />
              <Stat
                label="Conversione"
                value={`${weekConv}%`}
                highlight={weekConv >= 5}
              />
            </div>
            {weekSubs > 0 && (
              <div
                className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium"
                data-testid="lead-machine-week-badge"
              >
                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                {weekSubs === 1
                  ? "Hai ricevuto 1 richiesta questa settimana"
                  : `Hai ricevuto ${weekSubs} richieste questa settimana`}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Share modal — same look & feel as "Condividi preventivo e contratto" */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="lead-machine-share-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-2xl">
              Condividi link richiesta preventivo
            </DialogTitle>
            <DialogDescription>
              Scegli come inviarlo. Le clienti compilano la scheda e arrivano come lead nel CRM.
            </DialogDescription>
          </DialogHeader>

          {/* Link preview */}
          <div className="flex items-center gap-3 p-3 rounded-sm border bg-secondary/40 border-border">
            <LinkIcon className="w-4 h-4 text-neutral-400 shrink-0" strokeWidth={1.5} />
            <code className="flex-1 min-w-0 font-mono text-[11px] text-neutral-600 truncate" data-testid="lead-machine-url">
              {fullUrl}
            </code>
          </div>

          {/* Actions grid — identical visual language to ClientDetail share dialog */}
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            <ShareAction
              icon={LinkIcon}
              label="Copia link"
              hint="Incolla dove vuoi (bio Instagram, sito, …)"
              busy={busyAction === "link"}
              disabled={busyAction !== ""}
              onClick={copyLink}
              testid="lead-share-copy"
            />
            <ShareAction
              icon={MessageCircle}
              label="Invia via WhatsApp"
              hint="Apre WhatsApp con messaggio precompilato"
              disabled={busyAction !== ""}
              onClick={shareWhatsApp}
              testid="lead-share-whatsapp"
            />
            <ShareAction
              icon={Mail}
              label="Invia via Email"
              hint="Apre il client email con oggetto e testo"
              disabled={busyAction !== ""}
              onClick={shareEmail}
              testid="lead-share-email"
            />
            <ShareAction
              icon={Smartphone}
              label="Condividi…"
              hint={canWebShare ? "Menu nativo (AirDrop, Mail, WhatsApp…)" : "Non disponibile su questo dispositivo"}
              busy={busyAction === "system"}
              disabled={!canWebShare || busyAction !== ""}
              onClick={shareNative}
              testid="lead-share-system"
            />
          </div>

          <a
            href={fullUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1"
            data-testid="lead-share-preview"
          >
            <Eye className="w-3 h-3" strokeWidth={1.5} /> Anteprima pagina
            <ExternalLink className="w-3 h-3 ml-0.5" strokeWidth={1.5} />
          </a>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-xs text-neutral-500">{label}</div>
      <div
        className={`font-serif-heading text-3xl mt-1 tabular-nums ${
          highlight ? "text-emerald-600" : "text-neutral-900"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
